import { z } from 'zod';
import { api } from './api';
import { quoteResultActionSchema, type QuoteResultAction } from './quoteResultAction';
import { useAuthStore } from '@/stores/authStore';
import {
  assistantPrincipalSnapshot,
  sameAssistantPrincipal,
  type AssistantAuthState,
} from './assistantPrincipal';

const snapshotSchema = z.object({
  quoteResult: quoteResultActionSchema,
  current: z.object({
    id: z.string().uuid(),
    workOrderNumber: z.string().max(50).nullable(),
    status: z.string().min(1).max(50),
    updatedAt: z.string().datetime(),
  }),
});

/** Current principal before AND after async reads; rotation within the same session is permitted. */
export function quoteResultScope(
  action: QuoteResultAction,
  auth: AssistantAuthState = useAuthStore.getState()
) {
  if (
    !auth.isAuthenticated ||
    auth.isLocked ||
    !auth.accessToken ||
    auth.user?.id !== action.userId ||
    auth.user.tenantId !== action.tenantId ||
    !auth.hasPermission('work_orders.read')
  )
    return undefined;
  return assistantPrincipalSnapshot(auth);
}

export async function revalidateQuoteResult(action: QuoteResultAction) {
  const before = quoteResultScope(action);
  if (!before) throw new Error('quote_result_unavailable');
  const response = await api.get<unknown>(
    `/assistant/conversations/${action.conversationId}/quote-results/${action.actionId}`
  );
  const parsed = snapshotSchema.safeParse(response);
  const after = quoteResultScope(action);
  if (
    !after ||
    !sameAssistantPrincipal(before) ||
    !parsed.success ||
    parsed.data.current.id !== action.quoteId ||
    Object.keys(action).some(
      (key) =>
        action[key as keyof QuoteResultAction] !==
        parsed.data.quoteResult[key as keyof QuoteResultAction]
    )
  )
    throw new Error('quote_result_unavailable');
  return { ...parsed.data, scope: after };
}

/** Only the allowlisted editor destination; never interpret a result as a URL. */
export function quoteEditorPath(action: QuoteResultAction): string {
  return `/workorders/${action.quoteId}`;
}
